import { Component, Input, Output } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import type { Field } from '@modules/dynamic-form-builder/model/formBuilderInterface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiCallService } from '@services/api-call.service';
import { optionDirection } from '@enums/comman.enums';
import { EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ValidationErrorsComponent } from '@modules/dynamic-form-builder/components/validation-errors/validation-errors.component';
import { CommonService } from '@modules/shared/services/common.service';
import has from 'lodash/has';
import { InfoIconComponent } from '@components/info-icon/info-icon.component';

@Component({
  selector: 'app-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    InfoIconComponent
  ]
})
export class CheckboxComponent {
  @Input() field: Field;
  @Input() form: FormGroup;
  @Input() filterDropdownValues = {
    key: null,
    value: []
  };
  @Output() checkboxClicked = new EventEmitter<string>();
  @Output() optionFetched = new EventEmitter<void>();

  @Output() onClick = new EventEmitter<MouseEvent>();
  private destroy$ = new Subject<void>();
  optionDirectionEnum = optionDirection;
  originalDropdownData = [];
  constructor(
    private readonly apiCallService: ApiCallService,
    public readonly cs: CommonService
  ) {}

  ngOnInit() {
    if (this.field.optionsUrl && this.cs.isBrowser) {
      this.fetchAndSetOptions();
    }
  }
  ngOnChanges(changes) {
    if (
      changes &&
      has(changes, 'filterDropdownValues') &&
      this.originalDropdownData?.length
    ) {
      const data = this.getFilteredOptions();
      this.field.options = data;
    }
  }
  getFilteredOptions() {
    let data = this.originalDropdownData || [];
    if (
      this.filterDropdownValues?.key &&
      this.filterDropdownValues.value?.length
    ) {
      const filteredKeys = this.filterDropdownValues.value.map(
        (item) => item[this.filterDropdownValues.key]
      );
      data = this.originalDropdownData.filter((item) => {
        return !filteredKeys.includes(item[this.filterDropdownValues.key]);
      });
    }
    return data;
  }
  fetchAndSetOptions() {
    const url = this.field.optionsUrl;
    return this.apiCallService
      .get(url, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ data }) => {
        this.originalDropdownData = data;
        data = this.getFilteredOptions();
        this.field.options = data;
        this.optionFetched.emit();
      });
  }

  onCheckboxChange(option: any, event: any) {
    const control = this.form.get(this.field.name);
    const valueArray = control?.value || [];
    const valueKey = this.field.valueKey;
    if (event.target.checked) {
      if (!valueArray.includes(option[valueKey])) {
        valueArray.push(option[valueKey]);
      }
    } else {
      if (this.field.minSelected && valueArray.length <= this.field.minSelected) {
        event.target.checked = true;
        return;
      }
      const index = valueArray.indexOf(option[valueKey]);
      if (index > -1) {
        valueArray.splice(index, 1);
      }
    }

    control?.setValue(valueArray);
    this.checkboxClicked.emit(option.valueKey);
  }

  isChecked(option: any): boolean {
    const control = this.form.get(this.field.name);
    const valueArray = control?.value || [];
    return valueArray?.includes(option[this.field.valueKey]);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCheckboxClick(event: MouseEvent) {
    this.onClick.emit(event);
  }
}
